import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Card, Tabs, TextField, Button, Page } from '@shopify/polaris';
import { config } from '../constants';
import { authenticatedFetch } from '../utils/api';
import StoreHoursCard from './StoreHoursCard';
import LanguageCard from './LanguageCard';
const API_BASE = config.API_BASE;
const Notifications = lazy(() => import('./Notifications'));

export default function Settings() {
  // Track location.search and force re-render on popstate
  const [search, setSearch] = useState(window.location.search);

  useEffect(() => {
    const handler = () => setSearch(window.location.search);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  // Always get shop param from URL on every render
  const params = new URLSearchParams(search);
  const shopFromUrl = params.get('shop');
  const tabParam = params.get('tab');

  const tabIds = ['notifications', 'storehours', 'language']; // Add more tab ids if you add more tabs
  const selectedTab = tabIds.indexOf(tabParam) !== -1 ? tabIds.indexOf(tabParam) : 0;


  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [storeDomain, setStoreDomain] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [features, setFeatures] = useState([]);
  const [plan, setPlan] = useState('Free');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  // Always update storeDomain if shopFromUrl changes
  useEffect(() => {
    setStoreDomain(shopFromUrl || '');
    if (shopFromUrl) {
      // Fetch plan/features for tier restriction
      fetch(`${API_BASE}/current-plan?shop=${shopFromUrl}`)
        .then(res => res.json())
        .then(data => {
          setPlan(data?.plan || 'Free');
          import('../plans').then(mod => {
            setFeatures(mod.getResolvedFeatures(data?.plan || 'Free', mod.plans));
          });
        })
        .catch(() => setPlan('Free'));
    }
  }, [shopFromUrl]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    } else {
      setImageFile(null);
      setImagePreview(null);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setSuccess(false);
    setError('');
    let imageBase64 = null;
    if (imageFile) {
      imageBase64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });
    }
    try {
      const res = await authenticatedFetch(`${API_BASE}/support-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body, storeDomain, image: imageBase64, imageName: imageFile?.name, imageType: imageFile?.type })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSuccess(true);
        setSubject('');
        setBody('');
        setImageFile(null);
        setImagePreview(null);
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setError(data.error || 'Failed to send email');
        setSaveError(true);
        setTimeout(() => setSaveError(false), 3000);
      }
    } catch (e) {
      setError('Failed to send email');
      setSaveError(true);
      setTimeout(() => setSaveError(false), 3000);
    }
    setSending(false);
  };

  const tabs = [
    features.includes('Live chat') && {
      id: 'notifications',
      content: 'Live Chat Notifications',
      panelID: 'notifications-panel',
    },
    {
      id: 'storehours',
      content: 'Store Hours',
      panelID: 'storehours-panel',
    },
    {
      id: 'language',
      content: 'Language & Translation',
      panelID: 'language-panel',
    },
  ].filter(Boolean);

  return (
    <Page>
      {saveSuccess && (
        <div style={{ color: '#0a0', background: '#e6ffe6', borderRadius: 8, padding: '8px 16px', margin: '18px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span role="img" aria-label="success">✅</span> Settings saved successfully!
        </div>
      )}
      {saveError && (
        <div style={{ color: '#b00', background: '#ffeaea', borderRadius: 8, padding: '8px 16px', margin: '18px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span role="img" aria-label="error">⚠️</span> Failed to save settings. Please try again.
        </div>
      )}
      <Tabs
        tabs={tabs}
        selected={selectedTab}
        onSelect={idx => {
          const newTab = tabs[idx].id;
          const params = new URLSearchParams(window.location.search);
          params.set('tab', newTab);
          window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`);
          setSearch(window.location.search);
        }}
      >
        {selectedTab === 0 && features.includes('Live chat') && (
          <React.Fragment>
            <Notifications storeId={storeDomain} />
          </React.Fragment>
        )}
        {selectedTab === 1 && features.includes('Live chat') && (
          <div style={{ marginTop: 32 }}>
            <StoreHoursCard storeId={storeDomain} />
          </div>
        )}
        {selectedTab === 2 && (
          <div style={{ marginTop: 32, maxWidth: 500 }}>
            <LanguageCard storeId={storeDomain} />
          </div>
        )}
      </Tabs>
    </Page>
  );
}
